import _ from 'lodash';
import { ChildRelationshipEnum } from '@seeqdev/api/model/LockErrorPathOutputV1';
import type { LockErrorPathOutputV1 } from '@seeqdev/api/model/LockErrorPathOutputV1';

/** The only part of a lock error this module needs: its path of failure reasons. */
export interface LockErrorWithPath {
  itemPath?: LockErrorPathOutputV1[];
}

/**
 * Failure reasons that are shown only when a better, more specific reason is *not* also present on the same item's
 * path. Keyed by the vaguer reason, valued by the more specific reason that supersedes it.
 *
 * This encodes the same rules that used to live in the `showThisError` closures of
 * `LockFailureReasons.atom.tsx`; every other reason is always shown.
 */
const REASONS_SUPERSEDED_BY: Partial<Record<ChildRelationshipEnum, ChildRelationshipEnum>> = {
  [ChildRelationshipEnum.CURRENTWORKSTEPINAWORKSHEET]: ChildRelationshipEnum.REFERENCEDBYWORKSTEP,
  [ChildRelationshipEnum.WORKSHEET]: ChildRelationshipEnum.REFERENCEDBYJOURNALINWORKSHEET,
};

/**
 * Whether a single failure reason should be rendered for its lock error. A reason is hidden when it carries no
 * `childRelationship` or no `item` (nothing renderable), or when a more specific reason supersedes it.
 */
export function isFailureReasonVisible(failureReason: LockErrorPathOutputV1, lockError: LockErrorWithPath): boolean {
  const { childRelationship, item } = failureReason;
  if (!childRelationship || item === undefined) {
    return false;
  }

  const supersededBy = REASONS_SUPERSEDED_BY[childRelationship];
  return !supersededBy || !_.some(lockError.itemPath, (reason) => reason.childRelationship === supersededBy);
}

/**
 * The failure reasons that a lock error will actually render, in path order.
 *
 * Pulled out of the render path so that callers can reason about *how many* and *which* reasons an error will show
 * without rendering it - `getGroupSharedReason` in `groupLockErrorsByWorkbook.ts` needs exactly that in order to
 * decide whether a whole workbook group collapses to one summary line.
 */
export function getVisibleFailureReasons(lockError: LockErrorWithPath): LockErrorPathOutputV1[] {
  return _.filter(lockError.itemPath ?? [], (failureReason) => isFailureReasonVisible(failureReason, lockError));
}
